import React from 'react';

// Keys never shown to the clinician: internal id, and (when requested) the
// fields a day-group folds away because they vary per day.
const ALWAYS_HIDDEN = ['diary_item_id'];

// Render a proposal payload as readable key/value lines (shallow; nested values
// stringified). `omit` lets a grouped card hide the fields it summarises itself
// (e.g. day_of_week, shown as chips instead). Used as the fallback for any keys
// the structured view below doesn't recognise.
export function renderPayload(
  payload: Record<string, unknown>,
  omit: string[] = [],
): React.ReactNode {
  const hidden = new Set(ALWAYS_HIDDEN.concat(omit));
  const entries = Object.entries(payload).filter(([k]) => !hidden.has(k));
  if (entries.length === 0) return null;
  return (
    <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px' }}>
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          <span style={{ color: '#6b7280', fontSize: 12 }}>{key}</span>
          <span style={{ color: '#1f2937', fontSize: 12, wordBreak: 'break-word' }}>
            {value == null
              ? '—'
              : typeof value === 'object'
                ? JSON.stringify(value)
                : String(value)}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Structured diary view
//
// Diary create/update payloads carry the clinically meaningful content (meals,
// activities, supplements, macro targets) as nested objects. Dumping them as
// JSON is unreadable, so we lay them out in labelled sections a clinician can
// audit at a glance. Catalog references stay as ids for now — resolving names
// would need extra requests, which this first pass deliberately avoids.
// ----------------------------------------------------------------------------

// Keys the structured view renders itself; the rest fall through to renderPayload.
const STRUCTURED_KEYS = [
  'name', 'type', 'subtype', 'time_of_day', 'day_of_week', 'effective_date',
  'cancellation_date', 'hidden', 'meal_target', 'meals', 'new_meals',
  'activities', 'supplements', 'medications',
];

const DAY_ES: Record<string, string> = {
  Monday: 'Lunes', Tuesday: 'Martes', Wednesday: 'Miércoles', Thursday: 'Jueves',
  Friday: 'Viernes', Saturday: 'Sábado', Sunday: 'Domingo',
};

const SUBTYPE_ES: Record<string, string> = {
  Breakfast: 'Desayuno', Lunch: 'Comida', Dinner: 'Cena', Snack: 'Snack', Other: 'Otro',
  Cardio: 'Cardio', Gym: 'Gimnasio', Sport: 'Deporte', Swimming: 'Natación',
  Yoga: 'Yoga', Pilates: 'Pilates', Walking: 'Caminata', walking: 'Caminata',
  supplementation: 'Suplementación', medication: 'Medicación',
};

const LEVEL_ES: Record<string, string> = {
  low: 'Baja', moderate: 'Media', medium: 'Media', high: 'Alta',
};

const C = {
  label: '#6b7280',
  value: '#1f2937',
  muted: '#9ca3af',
  panelBg: '#f9fafb',
  panelBorder: '#eef0f2',
  chipBg: '#f1f5f9',
  chipFg: '#334155',
  accent: '#0f766e',
};

// Round to at most one decimal and drop a trailing ".0".
function fmtNum(v: unknown): string {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v ?? '');
  return String(Math.round(n * 10) / 10);
}

function asArray(v: unknown): Record<string, unknown>[] {
  return Array.isArray(v) ? (v.filter((x) => x && typeof x === 'object') as Record<string, unknown>[]) : [];
}

const Chip: React.FC<{ children: React.ReactNode; tone?: 'plain' | 'accent' }> = ({ children, tone = 'plain' }) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: 999,
      background: tone === 'accent' ? '#ecfdf5' : C.chipBg,
      color: tone === 'accent' ? '#059669' : C.chipFg,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: C.muted, marginBottom: 4 }}>
    {children}
  </div>
);

const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ border: `1px solid ${C.panelBorder}`, background: C.panelBg, borderRadius: 8, padding: '8px 10px' }}>
    {children}
  </div>
);

// Macro targets as a compact chip row. Water only shown when set (>0).
function renderMealTarget(t: Record<string, unknown>): React.ReactNode {
  const chips: { k: string; label: string }[] = [
    { k: 'calories', label: `${fmtNum(t.calories)} kcal` },
    { k: 'protein', label: `P ${fmtNum(t.protein)} g` },
    { k: 'carbs', label: `C ${fmtNum(t.carbs)} g` },
    { k: 'fat', label: `G ${fmtNum(t.fat)} g` },
    { k: 'fiber', label: `Fibra ${fmtNum(t.fiber)} g` },
  ];
  if (Number(t.water) > 0) chips.push({ k: 'water', label: `Agua ${fmtNum(t.water)}` });
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
      {chips.map((c) => (c.k in t ? <Chip key={c.k}>{c.label}</Chip> : null))}
    </div>
  );
}

function renderMeals(meals: Record<string, unknown>[]): React.ReactNode {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {meals.map((meal, i) => {
        const ingredients = asArray(meal.ingredients);
        return (
          <Panel key={i}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.value }}>
              {String(meal.name ?? `Comida ${i + 1}`)}
            </div>
            {meal.notes ? (
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2, fontStyle: 'italic' }}>{String(meal.notes)}</div>
            ) : null}
            {ingredients.length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {ingredients.map((ing, j) => (
                  <div key={j} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.label }}>
                    <span>Ingrediente #{String(ing.ingredient_id ?? '—')}</span>
                    <span style={{ color: C.value, fontWeight: 600 }}>{fmtNum(ing.quantity)} g</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        );
      })}
    </div>
  );
}

function renderActivities(activities: Record<string, unknown>[]): React.ReactNode {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {activities.map((a, i) => {
        const level = LEVEL_ES[String(a.activityLevel)] || (a.activityLevel ? String(a.activityLevel) : '');
        const meta = [
          level,
          a.expectedDurationMin != null ? `${fmtNum(a.expectedDurationMin)} min` : '',
          a.activityType != null ? `tipo #${String(a.activityType)}` : '',
        ].filter(Boolean);
        return (
          <Panel key={i}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.value }}>{String(a.name ?? `Actividad ${i + 1}`)}</span>
            </div>
            {meta.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 4 }}>
                {meta.map((m, j) => <Chip key={j}>{m}</Chip>)}
              </div>
            )}
            {a.details ? (
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.4 }}>{String(a.details)}</div>
            ) : null}
          </Panel>
        );
      })}
    </div>
  );
}

function renderCatalogList(
  items: Record<string, unknown>[],
  catalogKey: string,
  catalogLabel: string,
): React.ReactNode {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((it, i) => (
        <Panel key={i}>
          <div style={{ fontSize: 11.5, color: C.value, lineHeight: 1.4 }}>{String(it.description ?? '—')}</div>
          {it[catalogKey] != null && (
            <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
              {catalogLabel} #{String(it[catalogKey])}
            </div>
          )}
        </Panel>
      ))}
    </div>
  );
}

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginTop: 10 }}>
    <SectionLabel>{label}</SectionLabel>
    {children}
  </div>
);

// Structured, auditable view of a diary proposal payload. Falls back to the
// plain key/value renderer for any keys it doesn't model.
export const ProposalPayloadView: React.FC<{
  payload: Record<string, unknown>;
  omit?: string[];
}> = ({ payload, omit = [] }) => {
  const meals = asArray(payload.new_meals).length ? asArray(payload.new_meals) : asArray(payload.meals);
  const activities = asArray(payload.activities);
  const supplements = asArray(payload.supplements);
  const medications = asArray(payload.medications);
  const mealTarget = payload.meal_target && typeof payload.meal_target === 'object'
    ? (payload.meal_target as Record<string, unknown>)
    : null;

  // Compact meta tags (subtype / time / day / dates / hidden), unless omitted.
  const show = (k: string) => !omit.includes(k);
  const metaTags: React.ReactNode[] = [];
  if (show('subtype') && payload.subtype) metaTags.push(<Chip key="subtype">{SUBTYPE_ES[String(payload.subtype)] || String(payload.subtype)}</Chip>);
  if (show('time_of_day') && payload.time_of_day) metaTags.push(<Chip key="time">{String(payload.time_of_day)}</Chip>);
  if (show('day_of_week') && payload.day_of_week) metaTags.push(<Chip key="day" tone="accent">{DAY_ES[String(payload.day_of_week)] || String(payload.day_of_week)}</Chip>);
  if (show('effective_date') && payload.effective_date) metaTags.push(<Chip key="eff">Desde {String(payload.effective_date)}</Chip>);
  if (show('cancellation_date') && payload.cancellation_date) metaTags.push(<Chip key="cancel">Cancela {String(payload.cancellation_date)}</Chip>);
  if (show('hidden') && payload.hidden === true) metaTags.push(<Chip key="hidden">Oculto</Chip>);

  return (
    <div style={{ marginTop: 8 }}>
      {show('name') && payload.name ? (
        <div style={{ fontSize: 13, fontWeight: 600, color: C.value }}>{String(payload.name)}</div>
      ) : null}

      {metaTags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>{metaTags}</div>
      )}

      {mealTarget && <Section label="Objetivo nutricional">{renderMealTarget(mealTarget)}</Section>}
      {meals.length > 0 && <Section label={meals.length > 1 ? 'Comidas' : 'Comida'}>{renderMeals(meals)}</Section>}
      {activities.length > 0 && <Section label={activities.length > 1 ? 'Actividades' : 'Actividad'}>{renderActivities(activities)}</Section>}
      {supplements.length > 0 && <Section label={supplements.length > 1 ? 'Suplementos' : 'Suplemento'}>{renderCatalogList(supplements, 'supplement_catalog', 'Catálogo')}</Section>}
      {medications.length > 0 && <Section label={medications.length > 1 ? 'Medicaciones' : 'Medicación'}>{renderCatalogList(medications, 'medication_catalog', 'Catálogo')}</Section>}

      {/* Anything not modelled above (unknown scalar fields) still shows. */}
      {renderPayload(payload, [...omit, ...STRUCTURED_KEYS])}
    </div>
  );
};
